// Pure, framework-free helpers for the MediaBrowser (Browse tab: Pixabay images /
// Giphy gifs). Kept craft/redux/react-query-free so the crash-prone WHAT decisions
// are unit-testable without mounting the whole picker.
//
// QA-113 (Kelly, 2026-08-06):
//   * item 7 — "exiting Giphy often crashes the page". Root cause: the historical
//     inline mapper reached blindly into `d.images.fixed_height_small.url` and
//     `d.images.fixed_height_small_still.url`. Giphy returns rows that do not
//     always carry that exact nested variant, so the access threw a TypeError
//     inside a setState updater — an uncaught error that unmounts the whole
//     builder tree (white screen -> manual reload). `normalizeBrowserMediaItem`
//     guards every access and drops a row it cannot resolve, so a malformed row
//     can never crash the picker.
//   * item 2 — "Giphy toggle shows no options". `shouldShowInitialView` no longer
//     excludes the Giphy engine, so selecting Gifs with no search term shows the
//     same clickable keyword chips Pixabay shows (each chip runs a Giphy search)
//     instead of a blank grid.

/**
 * Normalise ONE raw media-browser API row to the `{ url, thumbnail }` shape the
 * MediaMasonry grid renders. Returns null for a row with no usable url so the
 * caller can filter it out (never render an <img> with an undefined src).
 *
 * @param {object} d      raw API row (Pixabay image row OR Giphy gif row)
 * @param {boolean} isGif true when the Giphy engine is selected
 * @returns {{url:string, thumbnail:string}|null}
 */
export function normalizeBrowserMediaItem(d, isGif) {
  if (!d || typeof d !== "object") return null;

  if (isGif) {
    const images = d.images || {};
    // Prefer the small animated variant, then fall back through the shapes Giphy
    // actually returns — any one of these missing must NOT throw.
    const url =
      images.fixed_height_small?.url ||
      images.fixed_height?.url ||
      images.downsized?.url ||
      images.original?.url ||
      null;
    if (!url) return null;
    const thumbnail =
      images.fixed_height_small_still?.url ||
      images.original_still?.url ||
      images.original?.url ||
      url;
    return { url, thumbnail };
  }

  // Pixabay (or any non-gif) row.
  const url = d.largeImageURL || d.url || null;
  if (!url) return null;
  return { url, thumbnail: d.thumb || url };
}

/**
 * Normalise a LIST of raw rows, dropping every row that cannot be resolved to a
 * usable url. Non-array input yields an empty list (never throws).
 *
 * @param {Array<object>} rows
 * @param {boolean} isGif
 * @returns {Array<{url:string, thumbnail:string}>}
 */
export function normalizeBrowserMediaList(rows, isGif) {
  if (!Array.isArray(rows)) return [];
  const out = [];
  for (const d of rows) {
    const item = normalizeBrowserMediaItem(d, isGif);
    if (item) out.push(item);
  }
  return out;
}

/**
 * Whether the Browse tab should show its initial keyword-chip prompt (the
 * "options to choose from") rather than a results grid. True while there is no
 * meaningful search term, for EVERY engine — including Giphy, which previously
 * showed a blank grid here (QA-113 item 2).
 *
 * @param {string} mediaKeyword
 * @param {number} searchLengthLimit
 * @returns {boolean}
 */
export function shouldShowInitialView(mediaKeyword, searchLengthLimit) {
  return !mediaKeyword || mediaKeyword.length <= searchLengthLimit;
}
